import type { DatasetStorageBackend } from "@/api/datasetStorageBackend";
import { isAnnotationBundleSet } from "@/api/trainingAnnotation";
import { AnnotationBundlePanel } from "./AnnotationBundlePanel";
import { IconButton, Spinner, TrackingPlayButton } from "@carbon6/c6-frontend-design";
import { PlayIcon, RotateCcwIcon, StopIcon } from "@carbon6/c6-frontend-design/icons";
import {
  MediaAnnotationStudio,
  type AnnotationMediaTarget,
  type AnnotationStudioSequenceActionRenderer,
} from "@carbon6/c6-frontend-design/annotation";
import { useEffect, useMemo, useRef, useSyncExternalStore, type ReactNode } from "react";
import { annotationUpstreamBlocked, gpuCapacityController } from "@/components/gpu";
import { HatTextTooltip } from "@/components/tooltip";
import { AnnotationGpuLockOverlay } from "./AnnotationGpuLockOverlay";
import { AnnotationSetLockOverlay } from "./AnnotationSetLockOverlay";
import { AnnotationSetPanel } from "./AnnotationSetPanel";
import { AnnotationStorageLock } from "./AnnotationStorageLock";
import { sameAnnotationMedia } from "./tracking/AnnotationTrackingController";
import type { AnnotationStudioBinding } from "./useAnnotationStudio";
import styles from "./AnnotationStudioTab.module.css";

export type AnnotationStudioTabProps = {
  binding: AnnotationStudioBinding;
  initialSetUuid?: string;
  // Annotation 을 지원하지 않는 저장 위치. 값이 있으면 스튜디오 대신 잠금 카드를 그린다. 그때 호스트는
  // binding 을 켜지 않는다. 그래서 세트를 조회하지 않는다.
  unsupportedBackend?: DatasetStorageBackend | null;
};

export function AnnotationStudioTab({
  binding,
  initialSetUuid,
  unsupportedBackend = null,
}: AnnotationStudioTabProps): ReactNode {
  const selectSet = binding.selectSet;
  const initialSelectionRef = useRef<string | null>(null);
  const datasetUuid = binding.importContext.datasetUuid;
  // driver 는 GPU holder 유무를 동작 시점에 읽는다. 하지만 화면(잠금 카드, Tracking 버튼)은 값이
  // 바뀔 때 다시 그려야 한다. 그래서 위젯과 같은 컨트롤러를 구독해 폴링 한 번에 함께 갱신한다.
  const gpuCapacity = useSyncExternalStore(
    gpuCapacityController.subscribe,
    gpuCapacityController.getSnapshot,
    gpuCapacityController.getSnapshot,
  );
  const gpuBlocked = annotationUpstreamBlocked(gpuCapacity);

  useEffect(() => {
    // 지원하지 않는 저장 위치에서는 세트를 조회하지 않는다. 조회하면 409 다.
    if (initialSetUuid === undefined || unsupportedBackend !== null) {
      initialSelectionRef.current = null;
      return;
    }
    const key = `${datasetUuid}:${initialSetUuid}`;
    if (initialSelectionRef.current === key) return;
    initialSelectionRef.current = key;
    void selectSet(initialSetUuid);
  }, [datasetUuid, initialSetUuid, selectSet, unsupportedBackend]);

  const sequenceAction = useMemo<AnnotationStudioSequenceActionRenderer>(
    () => (context) => {
      if (context.streamId === null) return { mode: "available" };
      const target = { sequenceId: context.sequence.id, streamId: context.streamId };
      const action = trackingAction(binding, target, "icon");
      const active = sameAnnotationMedia(binding.trackingSnapshot.targetMedia, target);
      // canStart 안에도 GPU 판정이 있지만 여기서도 확인한다. 폴링 값이 바뀌면 이 함수의 identity 가
      // 바뀌고, c6 가 행 액션을 다시 그린다. binding 만 보면 그 변화를 놓친다.
      const startable = !gpuBlocked && binding.trackingController.canStart;
      return active
        ? { mode: "persistent", action }
        : { mode: "available", action: startable ? action : undefined };
    },
    [binding, gpuBlocked],
  );
  // 결과가 이 화면에 없으면(`elsewhere`) 재생과 재시도는 눌러도 보이는 변화가 없어 제거한다.
  // 반대로 진행 중인 실행의 중지는 이 자리에서만 할 수 있다. 에피소드 행 액션은 현재 선택된
  // 카메라를 기준으로 그린다(c6 `StudioContextRail`). 그래서 다른 카메라에서는 중지가 표시되지 않는다.
  const trackingTarget = binding.trackingSnapshot.targetMedia;
  const trackingElsewhere = binding.trackingActionScope === "elsewhere";
  const trackingInFlight =
    binding.trackingSnapshot.status === "starting" || binding.trackingSnapshot.status === "running";
  const timelineAction =
    trackingTarget === null || (trackingElsewhere && !trackingInFlight)
      ? undefined
      : trackingAction(binding, trackingTarget, "text", { elsewhere: trackingElsewhere });

  if (unsupportedBackend !== null) return <AnnotationStorageLock backend={unsupportedBackend} />;

  if (isAnnotationBundleSet(binding.selectedSet) && binding.selectedSet !== null)
    return (
      <section className={styles.tab} aria-label="Annotation 스튜디오">
        <AnnotationSetPanel binding={binding} gpuLocked={gpuBlocked} />
        <AnnotationBundlePanel key={binding.selectedSet.uuid} set={binding.selectedSet} />
      </section>
    );

  // GPU 가 없어도 세트 선택과 다운로드는 쓸 수 있게 선택 줄을 남긴다. 나머지 세트 작업은 잠근다.
  if (gpuBlocked)
    return (
      <section className={styles.tab} aria-label="Annotation 스튜디오">
        <AnnotationSetPanel binding={binding} gpuLocked />
        <AnnotationGpuLockOverlay />
      </section>
    );

  return (
    <section className={styles.tab} aria-label="Annotation 스튜디오">
      <div className={styles.studio}>
        {/* 저장 범위를 먼저 고른 뒤 카메라·에피소드를 탐색하도록 좌측 선두에 배치한다. */}
        <MediaAnnotationStudio
          controller={binding.controller}
          mediaResult={binding.mediaResult}
          slots={{
            timelineAction,
            sequenceAction,
            contextLead: <AnnotationSetPanel binding={binding} />,
            toolsOverlay:
              binding.selectedSet?.status === "committed" ? (
                <AnnotationSetLockOverlay binding={binding} />
              ) : undefined,
          }}
        />
      </div>
    </section>
  );
}

/**
 * 두 자리는 크기가 달라 표현도 다르다. 에피소드 행의 액션 칸은 2rem 고정이다(c6
 * `CompositeListActionCell`). 텍스트 버튼을 넣으면 라벨이 줄바꿈되어 행 높이가 바뀐다. 그래서
 * 그 자리는 아이콘 + 툴팁으로 그리고, 폭이 넓은 타임라인 자리는 텍스트로 그린다.
 */
function trackingAction(
  binding: AnnotationStudioBinding,
  target: AnnotationMediaTarget,
  variant: "icon" | "text",
  { elsewhere = false }: { elsewhere?: boolean } = {},
): ReactNode {
  const tracking = binding.trackingSnapshot;
  const active = sameAnnotationMedia(tracking.targetMedia, target);

  if (active && tracking.status === "starting") {
    return renderAction(variant, {
      label: "Tracking 준비 중…",
      icon: <Spinner size="sm" />,
      disabled: true,
    });
  }
  if (active && tracking.status === "running") {
    return renderAction(variant, {
      label: "Tracking 중지",
      // 진행량은 라벨이 아니라 설명에 넣는다. framesSeen 은 프레임마다 늘어서, 라벨에 넣으면
      // 버튼 폭이 계속 바뀐다.
      description: elsewhere
        ? `다른 화면에서 ${tracking.framesSeen}프레임 추적 중`
        : `${tracking.framesSeen}프레임 추적됨`,
      icon: <StopIcon aria-hidden="true" />,
      tone: "warning",
      onClick: binding.trackingController.stop,
    });
  }
  if (active && tracking.status === "error") {
    return renderAction(variant, {
      label: "Tracking 재시도",
      description: tracking.error,
      icon: <RotateCcwIcon aria-hidden="true" />,
      tone: "danger",
      onClick: binding.trackingController.retry,
    });
  }
  if (
    active &&
    (tracking.status === "complete" || tracking.status === "stopped") &&
    tracking.framesSeen > 0
  ) {
    return renderAction(variant, {
      label: "Tracking 재생",
      icon: <PlayIcon aria-hidden="true" />,
      onClick: binding.trackingController.replay,
    });
  }
  return renderAction(variant, {
    label: "Tracking 시작",
    icon: <PlayIcon aria-hidden="true" />,
    start: true,
    disabled: !binding.trackingController.canStart,
    onClick: () => void binding.trackingController.start(target),
  });
}

type TrackingActionSpec = {
  label: string;
  icon: ReactNode;
  description?: string | null;
  tone?: "warning" | "danger";
  start?: boolean;
  disabled?: boolean;
  onClick?: () => void;
};

function renderAction(
  variant: "icon" | "text",
  { label, icon, description, tone, start = false, disabled = false, onClick }: TrackingActionSpec,
): ReactNode {
  if (variant === "text") {
    return (
      <IconButton
        text={label}
        title={description ?? undefined}
        size="sm"
        variant="surface"
        tone={tone}
        disabled={disabled}
        onClick={onClick}
      >
        {icon}
      </IconButton>
    );
  }

  // 시작 버튼은 c6 가 이 자리에 쓰는 전용 어포던스를 그대로 따른다.
  const button = start ? (
    <TrackingPlayButton size="sm" aria-label={label} disabled={disabled} onClick={onClick}>
      {icon}
    </TrackingPlayButton>
  ) : (
    <IconButton
      size="sm"
      variant="surface"
      tone={tone}
      // 목록 액션 셀은 hover/active 상태를 중립층으로 덮는다. warning 톤은 이 마커가 있어야만
      // 그 위에 다시 표시된다(c6 `compositeList`/`listViewShell`).
      data-studio-list-warning={tone === "warning" ? "" : undefined}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
    >
      {icon}
    </IconButton>
  );

  return (
    <HatTextTooltip text={description ? `${label} · ${description}` : label} placement="right">
      {button}
    </HatTextTooltip>
  );
}
